import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import partition from "lodash/partition";
import { AUDIT_ENGAGEMENT_ID_QUERY_PARAM } from "PFApp/audit_planner/audit_planner.utils";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_AVAILABILITY_KEY,
  FIELD_ID_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_QUESTIONS_TOGGLE_KEY,
  FIELD_METADATA_KEY,
  FIELD_PARENT_ACTIVITY_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { sanitizeFormDataForAPI } from "PFApp/helpers/activity_form/data_conversions";
import { useGetCustomFieldsToSave } from "PFApp/hooks/activity_form/use_custom_fields_to_save";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { ActivityFormType } from "PFApp/types/activity_form/activity_form_schema";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useActivitiesInvalidate } from "PFCore/hooks/queries/activities/use_activities_invalidate";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useAdminTargetAllocationInvalidate } from "PFCore/hooks/queries/admin/target_allocations/use_target_allocation_invalidate";
import { useAttachmentsInvalidate } from "PFCore/hooks/queries/attachments/use_attachments_invalidate";
import { useBookingsFromAvailabilityInvalidate } from "PFCore/hooks/queries/bookings/bookings_from_availability/use_bookings_from_availability_invalidate";
import { useCustomValuesInvalidate } from "PFCore/hooks/queries/custom_fields/admin";
import { useEngagementsInvalidate } from "PFCore/hooks/queries/engagements";
import { useMatchesInvalidate } from "PFCore/hooks/queries/matches/use_matches_invalidate";
import { usePhasesInvalidate } from "PFCore/hooks/queries/phases/use_phases";
import { useRolesInvalidate } from "PFCore/hooks/queries/roles/use_roles_invalidate";
import { useTargetAllocationInvalidate } from "PFCore/hooks/queries/target_allocation/use_target_allocation_invalidate";
import { createActivity } from "PFCore/services/activities";
import { ActivityData, GIdTargetType } from "PFTypes";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { ConfirmationModals } from "../../activity_form.types";
import { useActivityFormStore, useActivityFormStoreActions } from "../../activity_form_store";
import { ActivityFormState } from "../../activity_form_store/activity_form_store.types";
import { WORKFLOW_STATUSES_WITH_BOOKINGS } from "../../helpers";

export const useActivityFormSubmit = () => {
  const { t: tActivities } = useTranslation("activities");
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const navigate = useNavigate();
  const location = useLocation();
  const { showGrowl } = useGrowlStoreActions();
  const { getCustomFieldsToSave } = useGetCustomFieldsToSave();

  const {
    formState: { dirtyFields },
    handleSubmit
  } = useFormContext();

  const areQuestionsModified =
    !!dirtyFields[FIELD_INTEREST_QUESTIONS_KEY] || !!dirtyFields[FIELD_INTEREST_QUESTIONS_TOGGLE_KEY];

  const template = useActivityFormStore((state) => state.template);
  const initialActivity = useActivityFormStore((state) => state.activity);
  const activityCreator = useActivityFormStore((state) => state.activityCreator);
  const { isNew, isEdit, isAudit, isEngagement, isRole } = useActivityFormStore(
    (state) => state.activityStatus
  );
  const modals = useActivityFormStore((state) => state.modals);
  const { setModalDisplayed, setModalConfirmed, setSubmitOptions } = useActivityFormStoreActions();

  const { update: updateActivity } = useActivityUpdate(initialActivity.id);
  const { invalidate: invalidateBookingsFromAvailability } = useBookingsFromAvailabilityInvalidate();
  const { invalidate: invalidateActivityPhases } = usePhasesInvalidate();
  const { invalidate: invalidateAttachments } = useAttachmentsInvalidate();
  const { invalidateAuditRoles, invalidateRoles, invalidateFilteredRoles } = useRolesInvalidate();
  const { invalidate: invalidateMatches } = useMatchesInvalidate();
  const { invalidateAll: invalidateAllEngagements } = useEngagementsInvalidate();
  const { invalidateAllAllocationWindows } = useTargetAllocationInvalidate();
  const { invalidateAllRoles: invalidateAllAllocationWindowRoles, invalidateAllFallbackRoles } =
    useAdminTargetAllocationInvalidate();

  const { invalidate: invalidateCustomValues } = useCustomValuesInvalidate();
  const { invalidateByCollection: invalidateActivities } = useActivitiesInvalidate();

  const { getSingleErrorByType } = useResponseErrors();
  const { setError, clearErrors } = useFormContext();

  return (options: ActivityFormState["submitOptions"] = {}) => {
    // by default react-hook-form clears errors on submit only for registered fields
    // and api might return errors for other ones like metadata.booking_category_id
    clearErrors();
    setSubmitOptions(options);

    return handleSubmit(
      async (values: ActivityFormType) => {
        const sanitizedData = sanitizeFormDataForAPI({
          values,
          isNew,
          fieldsBlacklist: [FIELD_ID_KEY, FIELD_ASSIGNEE_ID_KEY]
        });
        const data = {
          ...sanitizedData,
          custom_fields: getCustomFieldsToSave(sanitizedData.custom_fields || [], activityCreator)
        };

        if (
          !isEngagement &&
          !options.draft &&
          (isEmpty(values[FIELD_METADATA_KEY]) ||
            (template.key === "role" && isEmpty(values[FIELD_METADATA_KEY].availability))) &&
          data.custom_fields.every((field) => isEmpty(field.values)) &&
          !modals[ConfirmationModals.NoRequirements].isConfirmed
        ) {
          setModalDisplayed(ConfirmationModals.NoRequirements, true);
          return;
        }

        if (isNew && areQuestionsModified && !modals[ConfirmationModals.QuestionsChanged].isConfirmed) {
          setModalDisplayed(ConfirmationModals.QuestionsChanged, true);
          return;
        }

        const isAvailabilityChanged = () => {
          const dataAvailability = {
            ...(data.metadata?.availability || {}),
            availability_threshold: undefined
          };
          const activityAvailability = {
            ...(initialActivity.metadata.availability || {}),
            availability_threshold: undefined
          };
          return !isEqual(dataAvailability, activityAvailability);
        };

        if (
          isEdit &&
          isAvailabilityChanged() &&
          WORKFLOW_STATUSES_WITH_BOOKINGS.includes(initialActivity.workflow_state) &&
          !modals[ConfirmationModals.Misalignment].isConfirmed
        ) {
          setModalDisplayed(ConfirmationModals.Misalignment, true);
          return;
        }

        const previousAssigneeId = isNew ? null : (initialActivity.assignee?.profile?.id ?? null);
        const currentAssigneeId = values[FIELD_ASSIGNEE_ID_KEY] ?? null;
        const assigneeIdChanged = currentAssigneeId !== previousAssigneeId;

        const apiCallFunction = isNew ? createActivity : updateActivity;
        try {
          const activity = await apiCallFunction({
            ...data,
            ...(assigneeIdChanged ? { assignee_id: currentAssigneeId } : {}),
            ...(initialActivity.state === "complete" ? {} : { state: options.draft ? "draft" : "new" })
          } as ActivityData);

          if (!activity) {
            return;
          }

          const { navigateCallback } = options;
          if (navigateCallback) {
            navigateCallback(activity);
          } else {
            const utmSource = location?.state?.utmSource;
            if (isAudit) {
              const auditEngagementId = isEngagement ? activity.id : values[FIELD_PARENT_ACTIVITY_KEY]?.id;
              const queryParams = new URLSearchParams({
                [AUDIT_ENGAGEMENT_ID_QUERY_PARAM]: `${auditEngagementId}`
              });
              navigate(`/audit_planner?${queryParams.toString()}`);
            } else if (template.need_matches) {
              navigate(`/activities/${activity.id}/matches`, { state: utmSource });
            } else {
              navigate(`/activities/${activity.id}`, { state: utmSource });
            }
          }
          invalidateBookingsFromAvailability(activity.id);
          invalidateActivityPhases(activity.id);
          invalidateAttachments(GIdTargetType.Task, activity.id);
          invalidateCustomValues();
          invalidateActivities();
          invalidateAllEngagements();
          invalidateMatches([activity.id]);
          if (isRole) {
            if (isAudit) {
              invalidateAuditRoles();
              invalidateAllAllocationWindowRoles();
              invalidateAllAllocationWindows();
              invalidateAllFallbackRoles();
            } else {
              invalidateRoles();
            }
          }
          invalidateFilteredRoles({ filters: { fields: { parentActivityId: activity.id } } });

          const growlParams = {
            activityName: activity.name,
            templateName: tActivities(`templates.${template.key}`)
          };
          showGrowl({
            message: isNew
              ? t("successActivityCreated", growlParams)
              : t("successActivityUpdated", growlParams),
            kind: "success"
          });
        } catch ({ response }) {
          showGrowl({
            message: isNew ? t("errors.errorActivityNotCreated") : t("errors.errorActivityNotUpdated"),
            kind: "error"
          });
          const errors = getSingleErrorByType(response);
          const [availabilityKeys, otherKeys] = partition(Object.keys(errors), (key) =>
            key.includes("availability")
          );
          const availabilityError = {};
          availabilityKeys.forEach((key) => {
            availabilityError[key] = errors[key];
          });
          !isEmpty(availabilityError) && setError(FIELD_AVAILABILITY_KEY, availabilityError);

          otherKeys.forEach((key) => {
            setError(key === "assignee" ? FIELD_ASSIGNEE_ID_KEY : key, { message: errors[key] });
          });
        }
        setModalConfirmed(ConfirmationModals.NoRequirements, false);
        setModalConfirmed(ConfirmationModals.QuestionsChanged, false);
        setModalConfirmed(ConfirmationModals.Misalignment, false);
        setSubmitOptions({});
      },
      () => {
        setSubmitOptions({});
        showGrowl({
          message: isNew ? t("errors.errorActivityNotCreated") : t("errors.errorActivityNotUpdated"),
          kind: "error"
        });
      }
    )();
  };
};
